import {
    memo,
    useRef,
    type KeyboardEvent,
    type MouseEvent,
    type ReactNode,
    type Ref,
} from "react";
import classNames from "classnames";
import { ArrowDownIcon } from "@icons";
import { composeHandlers } from "@design-system/overlay/composeHandlers";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { markEventBlocked } from "@design-system/ViewTable/ViewTable.lib";
import { CellLabel } from "./PickerCellLabel";
import { resolveCellChevronMode, usePickerRoot } from "./PickerContext";
import { PickerTrigger } from "./PickerTrigger";
import {
    PickerTriggerClear,
    PickerTriggerClearSlot,
} from "./PickerTriggerClear";
import type {
    PickerCellTriggerButtonProps,
    PickerForcedState,
} from "./Picker.types";
import { isSearchCharacter } from "./isSearchCharacter";
import styles from "./PickerCellTrigger.module.scss";

/** Props of `Picker.CellTrigger`: a chromeless trigger that fills a table cell. */
export interface PickerCellTriggerProps {
    value?: ReactNode;
    placeholder?: ReactNode;
    /** Renders the value as plain text: no button, no chevron. */
    readOnly?: boolean;
    /** The "changed, not yet saved" wash of editable table cells. */
    pending?: boolean;
    /** Enter and Space open the popup while it is closed. */
    openOnEnter?: boolean;
    /** A printable key opens the popup and seeds a popup search with that key. */
    openOnType?: boolean;
    /** `true` keeps the chevron visible at rest, `false` drops it; unset follows the root's `chevron`. */
    showChevron?: boolean;
    /** Clear control beside the label; clearing commits `null` / `[]` without opening. */
    clearable?: boolean;
    /** Accessible name; required when `value` is a node without text. */
    "aria-label"?: string;
    /** The Button bag slice a cell honours: `className`, `disabled` and a wrapping `tooltip`. */
    buttonProps?: PickerCellTriggerButtonProps;
    /** Tables with roving focus pass -1 so only the focused row's trigger is a tab stop. */
    tabIndex?: number;
    ref?: Ref<HTMLButtonElement>;
    className?: string;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
    /** Playground-only: paints a state statically. */
    "data-forced-state"?: PickerForcedState;
}

/** Props of `Picker.CellContent`: the cell trigger with an "Add" empty state. */
export interface PickerCellContentProps extends Omit<
    PickerCellTriggerProps,
    "placeholder"
> {
    /** Copy of the empty-state affordance; a leading plus glyph accompanies it. */
    addText?: string;
    hideAddIcon?: boolean;
    /**
     * An empty `readOnly` cell renders nothing, so a locked cell does not look
     * actionable; `true` keeps a disabled "Add" whose lock `buttonProps.tooltip`
     * can explain.
     */
    showDisabledAdd?: boolean;
}

const isPrintableKey = (event: KeyboardEvent<HTMLButtonElement>) =>
    isSearchCharacter(event.key) &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.altKey;

const PickerCellTriggerImpl = ({
    value,
    placeholder = "Select",
    readOnly = false,
    pending = false,
    openOnEnter = true,
    openOnType = false,
    showChevron,
    clearable = false,
    "aria-label": ariaLabel,
    buttonProps,
    tabIndex,
    ref,
    className: ownClassName,
    onClick,
    onKeyDown,
    "data-forced-state": forcedState,
}: PickerCellTriggerProps) => {
    const root = usePickerRoot();
    const buttonRef = useRef<HTMLButtonElement>(null);
    const clearSlotRef = useRef<HTMLSpanElement>(null);
    const className = classNames(ownClassName, buttonProps?.className);
    const isButtonDisabled = buttonProps?.disabled === true;
    const hasValue =
        (value !== undefined && value !== null) || root.selectionLabel !== null;
    const label =
        value ?? root.selectionNode ?? root.selectionLabel ?? placeholder;
    const accessibleName =
        ariaLabel ??
        (typeof label === "string"
            ? undefined
            : (root.selectionLabel ?? undefined));
    const baseClassName = classNames(
        styles.cellTrigger,
        { [styles.cellTriggerPending]: pending },
        root.classNames.trigger,
    );
    // The clearable path moves the consumer className onto the group wrapper.
    const sharedClassName = classNames(
        baseClassName,
        clearable ? undefined : className,
    );
    const chevronMode = resolveCellChevronMode({ showChevron, root });
    const isChevronRendered = chevronMode !== "none";
    const isChevronForced = chevronMode === "always";

    if (readOnly) {
        return (
            <Tooltip content={buttonProps?.tooltip}>
                <span
                    className={classNames(
                        baseClassName,
                        styles.cellTriggerReadOnly,
                        className,
                    )}
                    aria-label={accessibleName}
                >
                    <CellLabel label={label} isPlaceholder={!hasValue} />
                </span>
            </Tooltip>
        );
    }

    const handleKeyDown = ({
        event,
        isPopupOpen,
    }: {
        event: KeyboardEvent<HTMLButtonElement>;
        isPopupOpen: boolean;
    }) => {
        if (isPopupOpen || event.defaultPrevented) {
            return;
        }
        if (event.key === "Enter" || event.key === " ") {
            // Owning the activation keys (instead of the native button click)
            // keeps Enter / Space deterministic and lets openOnEnter opt out.
            event.preventDefault();
            if (openOnEnter) {
                root.openPopup();
            }
            return;
        }
        if (openOnType && isPrintableKey(event)) {
            event.preventDefault();
            root.setInputValue?.(event.key);
            root.openPopup();
        }
    };

    const trigger = (
        <PickerTrigger
            // Top level so `Tooltip` sees the disabled button and hovers a
            // wrapper instead; the rendered button is disabled either way.
            disabled={isButtonDisabled || root.isDisabled}
            render={(triggerProps, state) => (
                <button
                    type="button"
                    {...triggerProps}
                    disabled={isButtonDisabled || state.disabled}
                    tabIndex={tabIndex ?? triggerProps.tabIndex}
                    ref={mergeRefs([triggerProps.ref, ref, buttonRef])}
                    className={classNames(
                        triggerProps.className,
                        sharedClassName,
                    )}
                    data-picker-part="cell-trigger"
                    data-show-chevron={isChevronForced ? "" : undefined}
                    data-placeholder={hasValue ? undefined : ""}
                    data-forced-state={forcedState ?? root.forcedState}
                    aria-label={accessibleName}
                    aria-invalid={root.isInvalid || undefined}
                    onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        markEventBlocked(event);
                        composeHandlers(triggerProps.onClick, onClick)(event);
                    }}
                    onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
                        composeHandlers(
                            triggerProps.onKeyDown,
                            onKeyDown,
                        )(event);
                        handleKeyDown({ event, isPopupOpen: state.open });
                    }}
                    onBlur={composeHandlers(
                        triggerProps.onBlur,
                        root.onTriggerBlur,
                    )}
                >
                    <CellLabel label={label} isPlaceholder={!hasValue} />
                    {clearable && <PickerTriggerClearSlot ref={clearSlotRef} />}
                    {isChevronRendered && (
                        <ArrowDownIcon
                            className={styles.cellChevron}
                            data-picker-chevron=""
                            aria-hidden
                        />
                    )}
                </button>
            )}
        />
    );

    const withTooltip = (
        <Tooltip content={buttonProps?.tooltip}>{trigger}</Tooltip>
    );
    if (!clearable) {
        return withTooltip;
    }
    return (
        <span className={classNames(styles.cellTriggerGroup, className)}>
            {withTooltip}
            <PickerTriggerClear slotRef={clearSlotRef} triggerRef={buttonRef} />
        </span>
    );
};

/**
 * Chromeless trigger for ViewTable and spreadsheet cells: fills the cell,
 * shows its chevron only on hover / focus / open, marks its click as blocked
 * so the row does not toggle selection, and opens on Enter, Space and
 * (opt-in) typing. `readOnly` renders the label as plain text (still under
 * `buttonProps.tooltip`); `clearable`
 * wraps the button in a positioning span with a sibling clear control
 * measured onto the slot the button reserves, and the consumer `className`
 * (the `--picker-trigger-padding-x` override hatch) moves to that span so
 * the button still inherits the override. Memoised, so a table re-rendering
 * a closed cell skips it while its props and the root context hold.
 */
export const PickerCellTrigger = memo(PickerCellTriggerImpl);
